import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';

// Lo que el socio que inició sesión debe hoy: aportes, paseo, préstamos y
// multas. Usa el mismo cálculo que el Reporte de Deudas de administración.

const formatCurrency = (n) =>
  parseFloat(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const NOMBRES_MES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
  'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const nombreMes = (m) => `${NOMBRES_MES[(m.numeroMes || 1) - 1]} ${m.año}`;

const AlDia = () => <span className="text-success"><i className="fas fa-check-circle me-1"></i>Al día</span>;

const MisDeudas = () => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/reportes-prestamos/mis-deudas')
      .then((r) => setDatos(r.data))
      .catch(() => setError('No se pudo cargar lo que debes. Intenta más tarde.'))
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return <Card className="mb-4 shadow-sm"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Calculando lo que debes...</Card.Body></Card>;
  }
  if (error) return <Alert variant="warning" className="mb-4">{error}</Alert>;
  const f = datos?.data;
  if (!f) return null;

  const { reglas } = datos;
  const fecha = new Date(datos.fechaCorte).toLocaleDateString('es-ES');
  const alDia = f.totalAdeudado <= 0.009;

  return (
    <Card className="mb-4 shadow-sm border-0">
      <Card.Header className={`${alDia ? 'bg-success' : 'bg-danger'} text-white d-flex flex-wrap justify-content-between align-items-center gap-2`}>
        <h5 className="mb-0"><i className="fas fa-file-invoice-dollar me-2"></i>Lo que debo</h5>
        <div className="text-end">
          <small className="d-block opacity-75">Total al {fecha}</small>
          <span className="fs-4 fw-bold">{formatCurrency(f.totalAdeudado)}</span>
        </div>
      </Card.Header>
      <Card.Body>
        {alDia && <Alert variant="success" className="mb-3"><i className="fas fa-thumbs-up me-2"></i>¡Estás al día! No tienes deudas pendientes.</Alert>}

        <Row className="g-3 mb-3 text-center">
          {[
            { t: 'Aportes', v: f.aportes.adeudado, exento: f.exentoAportes, i: 'fa-hand-holding-usd' },
            { t: 'Paseo', v: f.paseo.adeudado, i: 'fa-bus' },
            { t: 'Préstamo', v: f.prestamoSaldo, i: 'fa-file-invoice' },
            { t: 'Multas', v: f.multasPendientes, i: 'fa-gavel' }
          ].map((x) => (
            <Col xs={6} md={3} key={x.t}>
              <div className="border rounded p-2 h-100">
                <small className="text-muted d-block"><i className={`fas ${x.i} me-1`}></i>{x.t}</small>
                {x.exento
                  ? <span className="text-info fw-bold">Exento</span>
                  : <span className={`fw-bold ${x.v > 0.009 ? 'text-danger' : 'text-success'}`}>{formatCurrency(x.v)}</span>}
              </div>
            </Col>
          ))}
        </Row>

        <Row className="g-3">
          <Col md={6}>
            <h6><i className="fas fa-hand-holding-usd me-1 text-warning"></i>Aportes <small className="text-muted">({formatCurrency(reglas.cuotaAporte)} al mes)</small></h6>
            {f.exentoAportes
              ? <small className="text-info">No pagas aportes (exento).</small>
              : f.aportes.meses.length === 0 ? <AlDia /> : (
                <ul className="mb-0 ps-3">
                  {f.aportes.meses.map((m) => <li key={m.mes}>{nombreMes(m)}: te falta <strong className="text-danger">{formatCurrency(m.falta)}</strong></li>)}
                </ul>
              )}

            <h6 className="mt-3"><i className="fas fa-bus me-1 text-warning"></i>Paseo <small className="text-muted">({formatCurrency(reglas.cuotaPaseo)} al mes)</small></h6>
            {f.paseo.meses.length === 0 ? <AlDia /> : (
              <ul className="mb-0 ps-3">
                {f.paseo.meses.map((m) => <li key={m.mes}>{nombreMes(m)}: te falta <strong className="text-danger">{formatCurrency(m.falta)}</strong></li>)}
              </ul>
            )}

            <h6 className="mt-3"><i className="fas fa-gavel me-1 text-secondary"></i>Multas pendientes</h6>
            {f.multas.length === 0 ? <AlDia /> : (
              <ul className="mb-0 ps-3">
                {f.multas.map((m, i) => <li key={i}>{m.motivo}: <strong className="text-danger">{formatCurrency(m.monto)}</strong></li>)}
              </ul>
            )}

            <h6 className="mt-3"><i className="fas fa-star me-1 text-info"></i>Programa pagado</h6>
            <div className="fw-bold text-success">{formatCurrency(f.pagado.programa)}</div>
            {f.pagado.detallePrograma.length === 0
              ? <small className="text-muted">Aún no has pagado programa.</small>
              : <small className="text-muted">{f.pagado.detallePrograma.map((x) => `${x.mes}: ${formatCurrency(x.monto)}`).join(' · ')}</small>}
          </Col>
          <Col md={6}>
            <h6><i className="fas fa-file-invoice me-1 text-primary"></i>Préstamos</h6>
            {f.prestamos.length === 0 ? <small className="text-muted">No tienes préstamos activos.</small> : f.prestamos.map((p) => (
              <div key={p.numeroSolicitud} className="border rounded p-2 mb-2">
                <div className="d-flex justify-content-between">
                  <strong>{p.numeroSolicitud}</strong>
                  {p.capitalAtrasado > 0.009 ? <Badge bg="warning" text="dark">Atrasado</Badge> : <Badge bg="success">Al día</Badge>}
                </div>
                <small className="text-muted d-block mb-1">Préstamo de {formatCurrency(p.monto)} en {p.plazo} cuotas de {formatCurrency(p.capitalMensual)} de capital</small>
                <Table size="sm" borderless className="mb-0 small">
                  <tbody>
                    <tr><td>Capital ya pagado</td><td className="text-end text-success">{formatCurrency(p.capitalPagado)}</td></tr>
                    <tr><td>Capital que falta</td><td className="text-end">{formatCurrency(p.capitalPendiente)}</td></tr>
                    {p.interesVencido > 0.009 && <tr><td>Interés hasta hoy</td><td className="text-end">{formatCurrency(p.interesVencido)}</td></tr>}
                    <tr className="fw-bold border-top"><td>Debes hoy</td><td className="text-end text-danger">{formatCurrency(p.saldoTotal)}</td></tr>
                  </tbody>
                </Table>
                {p.capitalAtrasado > 0.009 && (
                  <small className="text-warning d-block mt-1">
                    Vas atrasado {formatCurrency(p.capitalAtrasado)} de capital según tu calendario.
                  </small>
                )}
              </div>
            ))}
          </Col>
        </Row>
        <small className="text-muted d-block mt-3">
          Si crees que algún valor no es correcto, comunícate con la directiva.
        </small>
      </Card.Body>
    </Card>
  );
};

export default MisDeudas;
